<template>
    <div class="card-login-signup">
        <div class="card-login-signup-header">
            <img class="card-login-signup-header-logo" src="@/assets/icons/logo/Simuslogosmall.svg" />
            <div class="card-login-signup-content-title-subtitle">
                <h1 class="content-1 title-m">
                    <span class="semantic-highlight">{{ this.titleHighlight }} </span> {{ this.title }}
                </h1>
                <h2 class="content-1 body-l">{{ this.subtitle }}</h2>
            </div>
        </div>
        <div class="card-login-signup-content">
            <div class="card-login-signup-content-form">
                <div class="card-login-signup-content-form-input-container">
                    <InputTextFieldComponent v-model:value="this.emailValue" :inputType="'text'" label="Email" placeholder="Enter your email address"
                        :showIcon="false" />
                    <InputTextFieldComponent v-model:value="this.passwordValue" :inputType="'password'" label="Password" placeholder="Enter your password"
                        :showIcon="true"
                        :icon="this.iconSvg"
                        helperText="Helper text..." :showHelperText="false" />
                </div>
                <ButtonComponent :text="this.primaryActionButtonText" :onClickEvent="handlePrimaryAction" />
            </div>
            <hr />
            <div class="card-login-signup-bottom">
                <p class="content-1 label-l">{{ this.bottomText }}</p>
                <div>
                    <ButtonComponent
                        :text=this.secondaryActionButtonText
                        :type="buttonConstant.SECONDARY_TYPE_BUTTON" 
                        :size="buttonConstant.SMALL_SIZE_BUTTON" 
                        :onClickEvent="handleSecondaryAction" />
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.card-login-signup {
    width: 375px;
    padding: 24px 16px 16px 16px;
    border-radius: var(--border-radius-m);
    border: 1px solid var(--border-1);
    display: flex;
    flex-direction: column;
    gap: 24px;
    background-color: var(--background-1);
}

.card-login-signup-header {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.card-login-signup-header-logo {
    width: 56px;
}

.card-login-signup-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.card-login-signup-content-form {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.card-login-signup-content-form-input-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.card-login-signup-bottom {
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    justify-content: space-between;
}

@media screen and (max-width: 640px) {
    .card-login-signup {
        width: 100%;
    }
}
</style>

<script>
import * as buttonConstant from '@/constants/buttonConstant';

import { UserLoginEntitie } from '@/entities/userLoginEntitie';

import { userLoginDataStorage } from '@/storages/userLoginDataStorage';

import InputTextFieldComponent from '../core/input/InputTextFieldComponent.vue';
import ButtonComponent from '../core/button/ButtonComponent.vue';

export default {
    name: 'CardLoginSignup',
    components: {
        InputTextFieldComponent,
        ButtonComponent
    },
    setup() {
        const userLoginDataStorageInstance = userLoginDataStorage();

        return {userLoginDataStorageInstance, buttonConstant};
    },
    props: {
        title: {
            type: String,
            default: 'Welcome back'
        },
        titleHighlight: {
            type: String,
            default: '[s1mus]'
        },
        subtitle: {
            type: String,
            default: 'Log in with your account details'
        },
        primaryActionButtonText: {
            type: String,
            default: 'Log in'
        },
        secondaryActionButtonText: {
            type: String,
            default: 'Sign up'
        },
        bottomText: {
            type: String,
            default: "Don't have an account?"
        },
        primaryRedirect: {
            type: String,
            default: '/dashboard'
        },
        secondaryRedirect: {
            type: String,
            default: '/signup'
        }
    },
    data() {
        return {
            emailValue: '',
            passwordValue: '',
            iconSvg: `<svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path fill-rule='evenodd' clip-rule='evenodd' d='M20.2705 13.3285C16.9535 5.95743 7.04654 5.95743 3.72956 13.3285C3.54825 13.7314 3.07465 13.911 2.67173 13.7297C2.26882 13.5484 2.08918 13.0748 2.27049 12.6719C6.15351 4.04296 17.8465 4.04296 21.7296 12.6719C21.9109 13.0748 21.7312 13.5484 21.3283 13.7297C20.9254 13.911 20.4518 13.7314 20.2705 13.3285Z' fill=''/>
                    <path fill-rule='evenodd' clip-rule='evenodd' d='M12 11.8002C10.785 11.8002 9.80003 12.7851 9.80003 14.0002C9.80003 15.2153 10.785 16.2002 12 16.2002C13.2151 16.2002 14.2 15.2153 14.2 14.0002C14.2 12.7851 13.2151 11.8002 12 11.8002ZM8.20003 14.0002C8.20003 11.9015 9.9013 10.2002 12 10.2002C14.0988 10.2002 15.8 11.9015 15.8 14.0002C15.8 16.0989 14.0988 17.8002 12 17.8002C9.9013 17.8002 8.20003 16.0989 8.20003 14.0002Z' fill=''/>
                </svg>`,
        }
    },
    methods: {
        handlePrimaryAction() {
            if (this.primaryActionButtonText === "Log in") {
                if (this.emailValue && this.passwordValue) {
                    this.login();
                }
            } else {
                this.$router.push(this.primaryRedirect);
            }
        },
        handleSecondaryAction() {
            this.$router.push(this.secondaryRedirect);
        },
        login() {
            let username = this.emailValue;
            let password = this.passwordValue;

            this.$axios.login(username, password).then(response => {
                console.log("Login success:", response.data);
                
                this.userLoginDataStorageInstance.setUserLoginData(
                    UserLoginEntitie.fromJSON(response.data)
                );
                this.$router.push(this.primaryRedirect);
            }).catch(error => {
                console.error("Login error:", error);
            });
        }
    },
};
</script>